<template>
  <b-breadcrumb class="mb-0">
    <!-- v3 (founder 19.10.2026): no project name, a back arrow to it; its name shows on hover. -->
    <li v-if="compact" class="v3-back-item">
      <router-link
        :to="folderOrProjectItem ? folderOrProjectItem.to : groupRoutePath('/')"
        class="v3-back"
        v-b-tooltip.hover.bottom
        :title="folderOrProjectItem ? folderOrProjectItem.text : $t('webmag.dashboard')"
        :aria-label="$t('webmag.back')"
        data-test="header-back"
      >
        <b-icon icon="arrow-left" aria-hidden="true"></b-icon>
      </router-link>
    </li>
    <b-breadcrumb-item
      v-else-if="!folderOrProjectItem"
      :to="groupRoutePath('/')">
      <h2 class="mb-0">{{ $t('webmag.dashboard') }}</h2>
    </b-breadcrumb-item>
    <b-breadcrumb-item
      v-else
      :to="folderOrProjectItem.to">
      <h2
        :id="`tooltip-button-variant-${folderOrProjectItem.to}`"
        class="mb-0">
        {{ folderOrProjectItem.text | sliceStingPos30 }}
      </h2>
      <b-tooltip
        v-if="folderOrProjectItem.text.length > 30"
        :target="`tooltip-button-variant-${folderOrProjectItem.to}`"
      >
        {{ folderOrProjectItem.text }}
      </b-tooltip>
    </b-breadcrumb-item>
    <!-- v3 page (founder 17.10.2026): the name opens the list of pages next to it. -->
    <li v-if="compact && pageList.length > 1 && pageItem && !savingData && !allSaved" class="breadcrumb-item active">
      <b-dropdown
        variant="link"
        no-caret
        toggle-class="v3-page-switch"
        menu-class="v3-page-menu"
        data-test="header-pages"
      >
        <template v-slot:button-content>
          <h2 class="mb-0">{{ pageItem.text }}</h2>
          <b-icon icon="chevron-down" aria-hidden="true" class="v3-page-chevron"></b-icon>
        </template>
        <b-dropdown-header>{{ $t('webmag.headerPages') }}</b-dropdown-header>
        <b-dropdown-item
          v-for="p in pageList"
          :key="p.link"
          :to="p.link"
          :active="p.active"
          :link-class="{ 'v3-page-hidden': p.hidden }"
        >
          {{ p.title }}
          <b-icon v-if="p.active" icon="check2" aria-hidden="true" class="ml-auto"></b-icon>
        </b-dropdown-item>
      </b-dropdown>
    </li>
    <b-breadcrumb-item active v-else-if="pageItem && !savingData && !allSaved">
      <h2
        :id="`tooltip-button-variant-${pageItem.to}`"
        class="mb-0">
        {{ pageItem.text | sliceStingPos30 }}
      </h2>
      <b-tooltip
        v-if="pageItem.text.length > 30"
        :target="`tooltip-button-variant-${pageItem.to}`"
      >
        {{ pageItem.text }}
      </b-tooltip>
    </b-breadcrumb-item>
    <b-breadcrumb-item active v-if="pageItem && savingData && !allSaved">
      <h2 class="mb-0" style="color: #5cb85c">{{ $t('button.saving') }}</h2>
    </b-breadcrumb-item>
    <b-breadcrumb-item active v-if="pageItem && !savingData && allSaved">
      <h2 class="mb-0" style="color: #5cb85c">{{ $t('button.saved') }}</h2>
    </b-breadcrumb-item>
    <router-link
      data-page-step="4"
      v-if="!compact && !savingData && !allSaved && accessToPageSettings"
      class="settings-item mt-1 ml-2"
      :to="`${$route.path}/page-settings`">
      <b-icon icon="gear-fill" aria-hidden="true"></b-icon>
    </router-link>
  </b-breadcrumb>
</template>

<script>
import groupNavigation from '@/mixins/groupNavigation';
import SetAutosaveIndicatorForHeaderInCache from '@/graphQlQueries/mutations/setAutosaveIndicatorForHeaderInCache';
import SetButtonsDisabledOnAutosave from '@/graphQlQueries/mutations/setButtonsDisabledOnAutosave';
import GetAutosaveIndicatorForHeaderFromCache from '@/graphQlQueries/queries/getAutosaveIndicatorForHeaderFromCache';

export default {
  name: 'PageBreadcrumb',
  mixins: [groupNavigation],
  props: {
    pageItem: { type: Object },
    folderOrProjectItem: { type: Object },
    accessToPageSettings: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
  },
  data: () => ({
    savingData: false,
    allSaved: false,
    pageList: [],
  }),
  mounted() {
    this.$root.$on('header-page-list', this.setPageList);
    this.$root.$emit('header-page-list-frage');
  },
  beforeDestroy() {
    this.$root.$off('header-page-list', this.setPageList);
  },
  computed: {
    isRootFolder() {
      return !this.$route.params.parametersOfUrl;
    },
  },
  filters: {
    sliceStingPos30(value) {
      return (value.length > 30) ? `${value.slice(0, 30)}...` : value;
    },
  },
  apollo: {
    currentPageType: {
      query: GetAutosaveIndicatorForHeaderFromCache,
      update(data) {
        this.savingData = data.autosaveIndicator.isSavingData;
        if (data.autosaveIndicator.allSaved) {
          this.savingData = false;
          this.allSaved = data.autosaveIndicator.allSaved;
          // hide the publish query
          this.$apollo.mutate({
            mutation: SetButtonsDisabledOnAutosave,
            variables: {
              isButtonDisabled: true,
            },
          }).then(() => {
            setTimeout(() => { this.resetSaveQuery(); }, 1000);
          });
        }
      },
    },
  },
  methods: {
    setPageList(list) {
      this.pageList = list || [];
    },
    resetSaveQuery() {
      this.$apollo.mutate({
        mutation: SetAutosaveIndicatorForHeaderInCache,
        variables: {
          isSavingData: false,
          allSaved: false,
        },
      }).then(() => {
        // show the publish buttons
        this.$apollo.mutate({
          mutation: SetButtonsDisabledOnAutosave,
          variables: {
            isButtonDisabled: false,
          },
        });
      });
      this.savingData = false;
      this.allSaved = false;
    },
  },
};
</script>

<style scoped lang="scss">
.v3-back-item {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.v3-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.85);
}

.v3-back:hover,
.v3-back:focus-visible {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  outline: none;
}

.v3-page-switch,
::v-deep .v3-page-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  text-decoration: none;
}

::v-deep .v3-page-switch:hover h2,
::v-deep .v3-page-switch:focus h2 {
  color: #53DC86;
}

::v-deep .v3-page-chevron {
  width: 12px;
  color: rgba(255, 255, 255, 0.6);
}

::v-deep .v3-page-menu {
  min-width: 240px;
  max-width: 360px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px 0;
  border: 0;
  border-radius: 8px;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.08);

  .dropdown-header {
    padding: 8px 16px 4px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    font-size: 14px;
    color: #1f2937;
    white-space: normal;
  }

  .dropdown-item:hover,
  .dropdown-item:focus {
    background: #f3f4f6;
  }

  /* Hidden pages (popups) sit indented under the page before them (founder 22.10.2026). */
  .dropdown-item.v3-page-hidden {
    padding-left: 32px;
  }

  /* The current page: bold with a check, not Bootstrap's blue fill. */
  .dropdown-item.active,
  .dropdown-item:active {
    background: transparent;
    color: #111827;
    font-weight: 600;
  }
}
</style>
